import { useState, useCallback, useMemo, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { contactRequestService, CreateContactRequestPayload } from '../../services/contactRequestService';

/**
 * Chat step definitions
 */
export type ChatStep = 
  | 'greeting'
  | 'message'
  | 'phone'
  | 'budget'
  | 'move_in_date'
  | 'sending'
  | 'success'
  | 'error';

/**
 * Budget range options
 */
export interface BudgetRange {
  label: string;
  min: number;
  max: number;
}

/**
 * Move-in date options
 */
export interface MoveInOption {
  label: string;
  value: string; // ISO date string
}

/**
 * Chat message structure
 */
export interface ChatMessage {
  id: string;
  type: 'assistant' | 'user';
  content: string;
  timestamp: Date;
}

/**
 * Collected data from conversation
 */
export interface CollectedData {
  seekerMessage: string;
  seekerPhone: string;
  budgetMin?: number;
  budgetMax?: number;
  moveInDate?: string;
}

/**
 * Budget range options for selection
 * We adapt ranges depending on transaction type (vente vs location)
 */
const SALE_BUDGET_RANGES: BudgetRange[] = [
  { label: 'Moins de 50M XOF', min: 0, max: 50_000_000 },
  { label: '50M - 100M XOF', min: 50_000_000, max: 100_000_000 },
  { label: '100M - 200M XOF', min: 100_000_000, max: 200_000_000 },
  { label: '200M - 500M XOF', min: 200_000_000, max: 500_000_000 },
  { label: 'Plus de 500M XOF', min: 500_000_000, max: 999_999_999 },
];

const RENT_BUDGET_RANGES: BudgetRange[] = [
  { label: 'Moins de 200 000 XOF /mois', min: 0, max: 200_000 },
  { label: '200 000 - 350 000 XOF /mois', min: 200_000, max: 350_000 },
  { label: '350 000 - 500 000 XOF /mois', min: 350_000, max: 500_000 },
  { label: '500 000 - 800 000 XOF /mois', min: 500_000, max: 800_000 },
  { label: 'Plus de 800 000 XOF /mois', min: 800_000, max: 9_999_999 },
];

const getBudgetRanges = (transactionType?: 'sale' | 'rent'): BudgetRange[] => {
  if (transactionType === 'rent') {
    return RENT_BUDGET_RANGES;
  }
  // Default to sale-style tranches when inconnu
  return SALE_BUDGET_RANGES;
};

const STORAGE_NAMESPACE = 'sync-property-contact-chat';

/**
 * Move-in date options
 */
export const MOVE_IN_OPTIONS: MoveInOption[] = [
  { label: 'Dès que possible', value: new Date().toISOString() },
  { label: 'Dans 1-3 mois', value: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString() },
  { label: 'Dans 3-6 mois', value: new Date(Date.now() + 120 * 24 * 60 * 60 * 1000).toISOString() },
  { label: 'Dans plus de 6 mois', value: new Date(Date.now() + 240 * 24 * 60 * 60 * 1000).toISOString() },
];

/**
 * Generate assistant messages based on step, user name and transaction type
 */
const getAssistantMessage = (
  step: ChatStep,
  displayName: string,
  transactionType?: 'sale' | 'rent'
): string => {
  switch (step) {
    case 'greeting':
      return `Bonjour${displayName ? ` ${displayName}` : ''}, vous semblez intéressé par ce bien. Qu'est-ce qui vous attire dans cette propriété ?`;
    case 'phone':
      return 'Merci pour ces précisions ! Pour vous recontacter rapidement, quel est votre numéro de téléphone ?';
    case 'budget':
      return transactionType === 'rent'
        ? 'Avez-vous un budget de loyer mensuel à ne pas dépasser (pour ce bien ou des biens similaires) ?'
        : 'Avez-vous une idée de votre budget d’achat pour ce projet immobilier ?';
    case 'move_in_date':
      return 'Quand souhaiteriez-vous emménager ?';
    case 'sending':
      return 'Je transmets votre demande à notre équipe...';
    case 'success':
      return 'Parfait ! Votre demande a été transmise avec succès. Un conseiller vous recontactera très prochainement.';
    case 'error':
      return 'Oups, une erreur est survenue. Veuillez réessayer ou nous contacter directement.';
    default:
      return '';
  }
};

/**
 * Validate phone number (basic international format)
 */
const isValidPhone = (phone: string): boolean => {
  const cleaned = phone.replace(/[\s\-\(\)]/g, '');
  // Accept formats: +XXX..., 0X..., or just digits (min 8 chars)
  return /^(\+\d{1,3})?\d{8,14}$/.test(cleaned);
};

interface UsePropertyContactChatProps {
  propertyId: string;
  transactionType?: 'sale' | 'rent';
  onSuccess?: () => void;
}

/**
 * Hook for managing the property contact chatbot conversation
 */
export function usePropertyContactChat({ propertyId, transactionType, onSuccess }: UsePropertyContactChatProps) {
  const { user } = useAuth();
  
  // Normalize transactionType (handle both 'RENT'/'SALE' and 'rent'/'sale')
  const normalizedTransactionType = useMemo(() => {
    if (!transactionType) return undefined;
    const normalized = transactionType.toLowerCase();
    return normalized === 'rent' ? 'rent' : 'sale';
  }, [transactionType]);
  
  // User display name from auth context
  const displayName = useMemo(() => {
    if (!user) return '';
    const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    return fullName || '';
  }, [user]);

  // User email from auth context
  const userEmail = user?.email || '';
  
  // Full name for submission
  const fullName = useMemo(() => {
    if (!user) return '';
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    return name || user.email || '';
  }, [user]);

  // Current step in the conversation
  const [currentStep, setCurrentStep] = useState<ChatStep>('greeting');
  
  // Chat messages history
  const [messages, setMessages] = useState<ChatMessage[]>(() => [
    {
      id: 'greeting-1',
      type: 'assistant',
      content: getAssistantMessage('greeting', displayName, normalizedTransactionType),
      timestamp: new Date(),
    },
  ]);

  // Collected data
  const [collectedData, setCollectedData] = useState<CollectedData>({
    seekerMessage: '',
    seekerPhone: '',
  });

  // Current user input
  const [userInput, setUserInput] = useState('');
  
  // Loading state
  const [isTyping, setIsTyping] = useState(false);
  
  // Error state
  const [error, setError] = useState<string | null>(null);

  // Phone validation error
  const [phoneError, setPhoneError] = useState<string | null>(null);

  // Budget ranges adapted to transaction type
  const budgetRanges = useMemo(
    () => getBudgetRanges(normalizedTransactionType),
    [normalizedTransactionType]
  );

  // Storage key per user + property (fallback guest si pas connecté)
  const storageKey = useMemo(() => {
    const userId = (user as any)?.id ?? 'guest';
    return `${STORAGE_NAMESPACE}:${userId}:${propertyId}`;
  }, [user, propertyId]);

  // Loading state for DB restoration
  const [isLoadingFromDb, setIsLoadingFromDb] = useState(true);

  // Rehydrate conversation from DB first (if user is logged in), then localStorage fallback
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const loadConversation = async () => {
      setIsLoadingFromDb(true);

      // If user is logged in, try to load from DB first
      if (user?.email) {
        try {
          const existingRequest = await contactRequestService.getExistingByProperty(propertyId);
          
          if (existingRequest && existingRequest.messages && existingRequest.messages.length > 0) {
            // Convert DB messages to ChatMessage format
            // All messages including the success message should already be in the DB
            const dbMessages: ChatMessage[] = existingRequest.messages.map((msg) => ({
              id: msg.id,
              type: msg.author === 'ASSISTANT' ? 'assistant' : 'user',
              content: msg.content,
              timestamp: new Date(msg.createdAt),
            }));

            // Debug: log messages loaded from DB
            console.log('[Chatbot] Loaded messages from DB:', {
              totalMessages: dbMessages.length,
              messages: dbMessages.map(m => ({ type: m.type, content: m.content.substring(0, 50) + '...' })),
            });

            setMessages(dbMessages);
            
            // Restore collected data from the request
            setCollectedData({
              seekerMessage: existingRequest.seekerMessage || '',
              seekerPhone: existingRequest.seekerPhone || '',
              budgetMin: existingRequest.budgetMin,
              budgetMax: existingRequest.budgetMax,
              moveInDate: existingRequest.moveInDate,
            });

            // Set step to success since request was already submitted
            setCurrentStep('success');
            
            // Clear localStorage since we're using DB as source of truth
            try {
              window.localStorage.removeItem(storageKey);
            } catch {
              // Ignore localStorage errors
            }

            setIsLoadingFromDb(false);
            return;
          }
        } catch (error) {
          console.error('Error loading conversation from DB:', error);
          // Fall through to localStorage fallback
        }
      }

      // Fallback to localStorage if no DB request found or user not logged in
      try {
        const raw = window.localStorage.getItem(storageKey);
        if (raw) {
          const parsed = JSON.parse(raw) as {
            messages?: Omit<ChatMessage, 'timestamp'> & { timestamp: string }[];
            collectedData?: CollectedData;
            currentStep?: ChatStep;
          };

          if (parsed.messages && parsed.messages.length) {
            const restoredMessages: ChatMessage[] = parsed.messages.map((m) => ({
              ...m,
              timestamp: new Date(m.timestamp),
            }));
            setMessages(restoredMessages);
          }

          if (parsed.collectedData) {
            setCollectedData((prev) => ({ ...prev, ...parsed.collectedData }));
          }

          if (parsed.currentStep) {
            setCurrentStep(parsed.currentStep);
          }
        }
      } catch {
        // En cas de problème de parsing, on ignore et on repart sur une nouvelle conversation
      }

      setIsLoadingFromDb(false);
    };

    loadConversation();
    // eslint-disable-next-line
  }, [propertyId, user?.email, storageKey]);

  // Persister la conversation à chaque changement significatif
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const payload = {
      messages,
      collectedData,
      currentStep,
    };

    try {
      window.localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch {
      // Silencieux si stockage indisponible
    }
  }, [messages, collectedData, currentStep, storageKey]);

  /**
   * Add a message to the chat
   */
  const addMessage = useCallback((type: 'assistant' | 'user', content: string) => {
    const newMessage: ChatMessage = {
      id: `${type}-${Date.now()}`,
      type,
      content,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, newMessage]);
  }, []);

  /**
   * Simulate typing delay for assistant messages
   */
  const addAssistantMessage = useCallback(async (content: string) => {
    setIsTyping(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    setIsTyping(false);
    addMessage('assistant', content);
  }, [addMessage]);

  /**
   * Handle user message submission for step 1 (message)
   */
  const handleMessageSubmit = useCallback(async () => {
    if (!userInput.trim()) return;
    
    const message = userInput.trim();
    addMessage('user', message);
    setCollectedData(prev => ({ ...prev, seekerMessage: message }));
    setUserInput('');
    
    // Move to phone step
    setCurrentStep('phone');
    await addAssistantMessage(getAssistantMessage('phone', displayName, normalizedTransactionType));
  }, [userInput, addMessage, addAssistantMessage, displayName, transactionType]);

  /**
   * Handle phone submission for step 2
   */
  const handlePhoneSubmit = useCallback(async () => {
    const phone = userInput.trim();
    
    if (!isValidPhone(phone)) {
      setPhoneError('Veuillez entrer un numéro de téléphone valide');
      return;
    }
    
    setPhoneError(null);
    addMessage('user', phone);
    setCollectedData(prev => ({ ...prev, seekerPhone: phone }));
    setUserInput('');
    
    // TEMPORARILY SKIP BUDGET STEP - Move directly to move_in_date step
    // TODO: Uncomment to re-enable budget question
    // // Move to budget step
    // setCurrentStep('budget');
    // await addAssistantMessage(getAssistantMessage('budget', displayName, normalizedTransactionType));
    
    // Move directly to move-in date step
    setCurrentStep('move_in_date');
    await addAssistantMessage(getAssistantMessage('move_in_date', displayName, normalizedTransactionType));
  }, [userInput, addMessage, addAssistantMessage, displayName, normalizedTransactionType]);

  /**
   * Handle budget selection for step 3
   * TEMPORARILY COMMENTED - Budget question is disabled
   * TODO: Uncomment to re-enable budget question
   */
  // const handleBudgetSelect = useCallback(async (range: BudgetRange) => {
  //   addMessage('user', range.label);
  //   setCollectedData(prev => ({
  //     ...prev,
  //     budgetMin: range.min,
  //     budgetMax: range.max,
  //   }));
  //   
  //   // Move to move-in date step
  //   setCurrentStep('move_in_date');
  //   await addAssistantMessage(getAssistantMessage('move_in_date', displayName, normalizedTransactionType));
  // }, [addMessage, addAssistantMessage, displayName, transactionType]);

  /**
   * Handle skipping budget step
   * TEMPORARILY COMMENTED - Budget question is disabled
   * TODO: Uncomment to re-enable budget question
   */
  // const handleBudgetSkip = useCallback(async () => {
  //   addMessage('user', 'Je préfère ne pas préciser');
  //   
  //   // Move to move-in date step
  //   setCurrentStep('move_in_date');
  //   await addAssistantMessage(getAssistantMessage('move_in_date', displayName, normalizedTransactionType));
  // }, [addMessage, addAssistantMessage, displayName, transactionType]);
  
  // Temporary placeholder functions to prevent errors
  const handleBudgetSelect = useCallback(async (range: BudgetRange) => {
    // Temporarily disabled - no-op
  }, []);
  
  const handleBudgetSkip = useCallback(async () => {
    // Temporarily disabled - no-op
  }, []);

  /**
   * Submit the contact request to the API
   */
  const submitContactRequest = useCallback(async (data: CollectedData, currentMessages?: ChatMessage[]) => {
    setCurrentStep('sending');
    setIsTyping(true);
    
    try {
      // Use provided messages or fallback to state messages
      const messagesToUse = currentMessages || messages;
      
      // Prepare all messages including the success message that will be shown
      const successMessage = getAssistantMessage('success', displayName);
      
      // Add success message to local state first (for immediate UI feedback)
      const allMessages = [...messagesToUse];
      const successMsg: ChatMessage = {
        id: `success-${Date.now()}`,
        type: 'assistant',
        content: successMessage,
        timestamp: new Date(),
      };
      allMessages.push(successMsg);
      
      // Convert all messages (including success) to API format
      const messagesForApi = allMessages.map((msg) => ({
        author: msg.type === 'assistant' ? 'assistant' : 'client',
        content: msg.content,
        createdAt: msg.timestamp.toISOString(),
      }));

      // Debug: log messages being saved
      console.log('[Chatbot] Saving messages to DB:', {
        totalMessages: messagesForApi.length,
        messages: messagesForApi.map(m => ({ author: m.author, content: m.content.substring(0, 50) + '...' })),
      });

      const payload: CreateContactRequestPayload = {
        propertyId,
        seekerName: fullName,
        seekerEmail: userEmail,
        seekerPhone: data.seekerPhone,
        seekerMessage: data.seekerMessage,
        budgetMin: data.budgetMin,
        budgetMax: data.budgetMax,
        moveInDate: data.moveInDate,
        source: 'chatbot',
        messages: messagesForApi,
      };

      await contactRequestService.create(payload);
      
      // Update local state with all messages including success
      setMessages(allMessages);
      setIsTyping(false);
      setCurrentStep('success');
      
      // Clear localStorage since data is now in DB
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.removeItem(storageKey);
        } catch {
          // Ignore localStorage errors
        }
      }
      
      onSuccess?.();
    } catch (err) {
      setIsTyping(false);
      setCurrentStep('error');
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
      addMessage('assistant', getAssistantMessage('error', displayName));
    }
  }, [propertyId, fullName, userEmail, displayName, messages, storageKey, addMessage, onSuccess]);

  /**
   * Handle move-in date selection for step 4
   */
  const handleMoveInSelect = useCallback(async (option: MoveInOption) => {
    // Create user message
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: option.label,
      timestamp: new Date(),
    };
    
    // Update messages state immediately
    setMessages(prev => [...prev, userMessage]);
    
    // Update collected data
    const updatedData = {
      ...collectedData,
      moveInDate: option.value,
    };
    setCollectedData(updatedData);
    
    // Build updated messages array including the new user message
    const updatedMessages = [...messages, userMessage];
    
    // Submit with updated messages (including the new user message)
    await submitContactRequest(updatedData, updatedMessages);
  }, [messages, collectedData, submitContactRequest]);

  /**
   * Handle skipping move-in date step
   */
  const handleMoveInSkip = useCallback(async () => {
    // Create user message
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: 'Je ne sais pas encore',
      timestamp: new Date(),
    };
    
    // Update messages state immediately
    setMessages(prev => [...prev, userMessage]);
    
    // Build updated messages array including the new user message
    const updatedMessages = [...messages, userMessage];
    
    // Submit with updated messages (including the new user message)
    await submitContactRequest(collectedData, updatedMessages);
  }, [messages, collectedData, submitContactRequest]);

  /**
   * Reset the conversation
   */
  const resetChat = useCallback(() => {
    setCurrentStep('greeting');
    setMessages([
      {
        id: 'greeting-1',
        type: 'assistant',
        content: getAssistantMessage('greeting', displayName, normalizedTransactionType),
        timestamp: new Date(),
      },
    ]);
    setCollectedData({
      seekerMessage: '',
      seekerPhone: '',
    });
    setUserInput('');
    setError(null);
    setPhoneError(null);

    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(storageKey);
      } catch {
        // ignore
      }
    }
  }, [displayName, transactionType, storageKey]);

  /**
   * Handle main input submission based on current step
   */
  const handleSubmit = useCallback(async () => {
    switch (currentStep) {
      case 'greeting':
      case 'message':
        await handleMessageSubmit();
        break;
      case 'phone':
        await handlePhoneSubmit();
        break;
      default:
        break;
    }
  }, [currentStep, handleMessageSubmit, handlePhoneSubmit]);

  /**
   * Check if current step requires text input
   */
  const requiresTextInput = useMemo(() => {
    return ['greeting', 'message', 'phone'].includes(currentStep);
  }, [currentStep]);

  /**
   * Check if current step shows budget options
   * TEMPORARILY DISABLED - Budget question is commented out
   * TODO: Uncomment to re-enable: const showsBudgetOptions = currentStep === 'budget';
   */
  const showsBudgetOptions = false; // Temporarily disabled

  /**
   * Check if current step shows move-in options
   */
  const showsMoveInOptions = currentStep === 'move_in_date';

  /**
   * Check if conversation is complete
   */
  const isComplete = currentStep === 'success' || currentStep === 'error';

  /**
   * Get input placeholder based on current step
   */
  const inputPlaceholder = useMemo(() => {
    switch (currentStep) {
      case 'greeting':
        return 'Décrivez ce qui vous intéresse dans ce bien...';
      case 'phone':
        return 'Votre numéro de téléphone...';
      default:
        return 'Tapez votre message...';
    }
  }, [currentStep]);

  /**
   * Get input type based on current step
   */
  const inputType = currentStep === 'phone' ? 'tel' : 'text';

  return {
    // State
    messages,
    currentStep,
    userInput,
    isTyping,
    error,
    phoneError,
    isComplete,
    isLoadingFromDb,
    
    // Input config
    requiresTextInput,
    showsBudgetOptions,
    showsMoveInOptions,
    inputPlaceholder,
    inputType,
    
    // Options
    budgetRanges,
    moveInOptions: MOVE_IN_OPTIONS,
    
    // Actions
    setUserInput,
    handleSubmit,
    handleBudgetSelect,
    handleBudgetSkip,
    handleMoveInSelect,
    handleMoveInSkip,
    resetChat,
  };
}

export default usePropertyContactChat;

